/**
 * kbUi.tsx — shared presentational bits for the Knowledge Base surfaces.
 * Ported from prototype stage-kb-ingest.jsx (status pills, domain chips/pills,
 * confidence bar, snippet panels, helpers). No state — pure UI.
 */
import { I, type IconComponent } from "../../components/icons";
import { kbStore } from "../../lib/kbStore";
import type { KbDecision, KbDoc, KbDocStatus, KbScanOutcome } from "../../lib/mock";

export function kbRgba(hex: string, a: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

export function kbConfTone(c: number): string {
  return c >= 0.8 ? "var(--c-ok)" : c >= 0.6 ? "var(--c-warn)" : "var(--c-err)";
}

/**
 * How a contradiction's verdict is attributed — ONE component, used by the queue
 * row and by the review modal.
 *
 * The defect it closes (2026-09-14): both surfaces printed «Analisi AI» and
 * «{confidence}% confidenza» for every row. `D-53`'s targeted checks are decided
 * by a deterministic comparator that calls no model and writes `confidence = 1.0`
 * as a sentinel, so an arithmetic verdict was presented as an AI judgement that
 * was 100% sure — two false claims in one line, on the screen where an
 * administrator decides whether to publish a document.
 *
 * A rule is either certain of its own verdict or it does not fire, so there is
 * no percentage to show for one. A judge call has a real confidence and keeps it.
 * Two callers, one predicate: the attribution rule is stated here and nowhere
 * else.
 */
export function VerdictAttribution({
  method,
  confidence,
}: {
  method?: "deterministic" | "ai" | null;
  confidence: number;
}) {
  if (method === "deterministic") {
    return (
      <span
        style={{ fontSize: 12, color: "var(--c-ink-3)", fontWeight: 600 }}
        title="Confronto deciso da una regola: nessuna valutazione AI, nessun costo."
      >
        Regola deterministica
      </span>
    );
  }
  if (method === "ai") {
    return (
      <span style={{ fontSize: 12, color: kbConfTone(confidence), fontWeight: 600 }}>
        Valutazione AI · {Math.round(confidence * 100)}% confidenza
      </span>
    );
  }
  // Filed before the mechanism was recorded. Neither label is true of it, so it
  // gets neither — an unattributed row is better than a wrongly attributed one.
  return (
    <span style={{ fontSize: 12, color: "var(--c-ink-3)", fontWeight: 600 }}>
      Meccanismo non registrato
    </span>
  );
}

export function clip(s: string | null, n: number): string {
  if (!s) return "";
  return s.length > n ? s.slice(0, n).trimEnd() + "…" : s;
}

export const REC_LABEL: Record<KbDecision, string> = {
  accept_new: "Accetta nuovo",
  reject_new: "Mantieni esistente",
  merge: "Unisci",
  clarification: "Chiedi chiarimento",
};

/**
 * `D-53` / HON-2 (adversarial review, 2026-09-13) — what each decision
 * ACTUALLY does to a document's availability, one sentence per decision.
 *
 * Read off the server's own effect table (`admin.py:56-77`,
 * `_DECISION_EFFECTS`), not written beside the button:
 *
 *   accept_new    -> new `live`,     conflicting **archived**
 *   reject_new    -> new **rejected**, conflicting untouched
 *   merge         -> no status write at all
 *   clarification -> no status write at all
 *
 * Two of the four therefore REMOVE a live document from the assistant's
 * answers. Before this, the only sentences a decider saw were the queue
 * banner's «I documenti restano disponibili e citabili» — a blanket claim
 * false for half the queue — and an `accept_new` toast reading «… ora
 * disponibile per query AI», which under `D-53` is stale-true (the document
 * was already live) and named the ARCHIVED document nowhere on any surface.
 *
 * The «avviso» clause is conditional on purpose: `decide_review` writes only
 * `review_requests` plus document statuses — it never touches
 * `kb_claim_links` or `kb_comparisons`, and the answer-time dispute flag is
 * computed from the claim VALUES — so a merge/clarification decision closes
 * the queue row and leaves the warning the reader sees exactly where it was.
 */
export function decisionEffect(
  decision: KbDecision,
  names: { newTitle?: string | null; existingTitle?: string | null },
): string {
  const nuovo = names.newTitle?.trim() || "il documento nuovo";
  const esistente = names.existingTitle?.trim() || "il documento esistente";
  const invariato =
    "Entrambi i documenti restano disponibili: se i valori restano diversi, " +
    "l'avviso continua a comparire nelle risposte dell'assistente.";
  switch (decision) {
    case "accept_new":
      return (
        `${nuovo} resta disponibile; ${esistente} è stato ARCHIVIATO e non ` +
        "comparirà più nelle risposte dell'assistente."
      );
    case "reject_new":
      return (
        `${nuovo} è stato RIFIUTATO e non comparirà nelle risposte ` +
        `dell'assistente; ${esistente} resta disponibile.`
      );
    default:
      return invariato;
  }
}

export const DECISION_PILL: Record<KbDecision, { cls: string; label: string; muted?: boolean }> = {
  accept_new: { cls: "pill-ok", label: "Accettato" },
  reject_new: { cls: "", label: "Rifiutato", muted: true },
  merge: { cls: "pill-info", label: "Unito" },
  clarification: { cls: "pill-warn", label: "Richiesto chiarimento" },
};

const KB_STATUS: Record<KbDocStatus, { cls: string; label: string }> = {
  live: { cls: "pill-ok", label: "Live" },
  "pending-review": { cls: "pill-warn", label: "In revisione" },
  rejected: { cls: "", label: "Rifiutato" },
  ingesting: { cls: "pill-info", label: "Ingest…" },
  archived: { cls: "", label: "Archiviato" },
  "pending-manual-merge": { cls: "pill-info", label: "Da unire" },
};

export function KBStatusPill({ status }: { status: KbDocStatus }): JSX.Element {
  const s = KB_STATUS[status] ?? KB_STATUS.live;
  if (status === "ingesting") {
    return (
      <span className="pill pill-info" style={{ gap: 5 }}>
        <span
          className="spinner"
          style={{
            width: 9,
            height: 9,
            borderTopColor: "var(--c-info)",
            borderColor: "rgba(37,99,168,0.25)",
          }}
        />
        {s.label}
      </span>
    );
  }
  if (status === "rejected" || status === "archived") {
    return (
      <span className="pill" style={{ opacity: 0.55 }}>
        {s.label}
      </span>
    );
  }
  return <span className={`pill ${s.cls}`}>{s.label}</span>;
}

export function DomainDot({ color, size = 8 }: { color: string; size?: number }): JSX.Element {
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: color,
        display: "inline-block",
        flexShrink: 0,
      }}
    />
  );
}

export function DomainPill({ domainKey }: { domainKey: string }): JSX.Element {
  const d = kbStore.domain(domainKey);
  if (!d) return <span className="pill">{domainKey}</span>;
  return (
    <span
      className="pill"
      style={{
        background: kbRgba(d.color, 0.1),
        color: d.color,
        borderColor: kbRgba(d.color, 0.28),
      }}
    >
      <DomainDot color={d.color} size={7} />
      {d.label}
    </span>
  );
}

/**
 * The tender project a KB document came from.
 *
 * Shown as a SNAPSHOT, deliberately. The Knowledge Base and the tender backend use
 * separate databases, so this cannot be a foreign key: the name was copied at upload
 * time and the tender project may since have been renamed or deleted. Omar accepted
 * that constraint when he chose a real project link over a generic collection label
 * (2026-07-30), on the condition that the staleness stays visible — so the tooltip
 * says when the copy was taken rather than presenting it as the current name.
 *
 * Renders nothing when there is no project. Most documents have none: HR, IT and
 * Procurement material is filed by domain, not by tender.
 */
export function ProjectRefLabel({ doc }: { doc: KbDoc }): JSX.Element | null {
  if (!doc.projectName) return null;
  const taken = doc.projectSyncedAt
    ? new Date(doc.projectSyncedAt).toLocaleDateString("it-IT")
    : null;
  return (
    <span
      title={
        taken
          ? `Progetto registrato il ${taken}. Il nome è una copia di quel momento: ` +
            "se il progetto è stato rinominato, qui resta il nome vecchio."
          : "Il nome del progetto è una copia del momento del caricamento."
      }
      style={{
        display: "block",
        marginTop: 3,
        fontSize: 11,
        color: "var(--c-ink-3)",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: 180,
      }}
    >
      {doc.projectName}
    </span>
  );
}

const CHIP = { brand: "#1E3A5F", bg: "#FAFAF8", ink: "#3D424E", line: "#D6D3CC" };

export function DomainChip({
  label,
  color,
  active,
  onClick,
}: {
  label: string;
  color?: string;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  const activeBg = color || CHIP.brand;
  return (
    <button
      onClick={onClick}
      style={{
        appearance: "none",
        cursor: "pointer",
        fontFamily: "inherit",
        height: 26,
        padding: "0 11px",
        borderRadius: 13,
        fontSize: 12,
        fontWeight: 500,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        backgroundColor: active ? activeBg : CHIP.bg,
        color: active ? "#fff" : CHIP.ink,
        border: `1px solid ${active ? activeBg : CHIP.line}`,
        transition:
          "background-color var(--tr-fast), border-color var(--tr-fast), color var(--tr-fast)",
      }}
    >
      {color && <DomainDot color={active ? "#fff" : color} size={7} />}
      {label}
    </button>
  );
}

export function ConfidenceBar({
  value,
  width = 160,
}: {
  value: number;
  width?: number;
}): JSX.Element {
  const pct = Math.round(value * 100);
  const tone = kbConfTone(value);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div
        style={{
          width,
          height: 6,
          borderRadius: 3,
          background: "var(--c-bg-sunken)",
          overflow: "hidden",
          border: "1px solid var(--c-line)",
        }}
      >
        <div style={{ width: `${pct}%`, height: "100%", background: tone }} />
      </div>
      <span className="t-mono t-num" style={{ fontSize: 12, fontWeight: 600, color: tone }}>
        {pct}%
      </span>
    </div>
  );
}

export function SnippetPanel({
  tone,
  eyebrow,
  docId,
  source,
  text,
}: {
  tone: "new" | "existing";
  eyebrow: string;
  docId: string | null;
  source: string | null;
  text: string | null;
}): JSX.Element {
  const accent = tone === "new" ? "var(--c-ai)" : "var(--c-ink-3)";
  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        border: "1px solid var(--c-line)",
        borderRadius: 10,
        overflow: "hidden",
        background: "var(--c-bg)",
      }}
    >
      <div
        style={{
          padding: "8px 12px",
          borderBottom: "1px solid var(--c-line)",
          background: tone === "new" ? "var(--c-ai-soft)" : "var(--c-bg-sunken)",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: accent,
          }}
        >
          {eyebrow}
        </span>
        {docId && (
          <span
            className="t-mono"
            style={{ fontSize: 10.5, color: "var(--c-ink-3)", marginLeft: "auto" }}
          >
            {docId}
          </span>
        )}
      </div>
      <div style={{ padding: "12px 14px" }}>
        {text ? (
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: "var(--c-ink)" }}>
            “{text}”
          </p>
        ) : (
          <p style={{ margin: 0, fontSize: 12.5, color: "var(--c-ink-3)", fontStyle: "italic" }}>
            Nessun documento esistente in conflitto — segnalazione per contenuto off-topic.
          </p>
        )}
        {source && (
          <div style={{ marginTop: 10, fontSize: 11, color: "var(--c-ink-3)" }}>
            <I.File
              size={10}
              style={{ display: "inline", verticalAlign: "-1px", marginRight: 4 }}
            />
            {source}
          </div>
        )}
      </div>
    </div>
  );
}

export function KBPlaceholder({
  icon: Ico,
  title,
  body,
}: {
  icon: IconComponent;
  title: string;
  body: string;
}): JSX.Element {
  return (
    <div style={{ padding: "56px 24px", textAlign: "center" }}>
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 10,
          background: "var(--c-bg-sunken)",
          color: "var(--c-ink-3)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 14,
        }}
      >
        <Ico size={20} />
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 12.5, color: "var(--c-ink-3)", maxWidth: 380, margin: "0 auto" }}>
        {body}
      </div>
    </div>
  );
}

/**
 * `KBR-62` — «Controllo incompleto — limite di confronti raggiunto».
 *
 * ONE component for every surface that shows it: the document list, the
 * document's own row detail and the review queue. A second copy is how the
 * process panel and the review queue would come to say different things about
 * the same document — which is the failure this whole item is about, one level
 * up.
 *
 * It renders NOTHING for `complete` and for `unknown`, and that asymmetry is
 * deliberate rather than lazy. A badge on every healthy document is noise that
 * trains people to skip the line, and the one thing that must never happen is
 * for the alarm to be ignored on the day it fires. `unknown` is silent for a
 * different reason: it has nothing to report in either direction, and printing
 * «non sappiamo» beside a document nobody has scanned would read as a defect.
 *
 * The numbers are shown WHERE AVAILABLE, exactly as Omar asked, and are simply
 * omitted when the run did not record them — never printed as 0, because a
 * sentinel zero here reads as «zero comparisons were made», which is a
 * measurement rather than an absence.
 */
export function ScanIncompleteNotice({
  scan,
  compact = false,
}: {
  scan: KbScanOutcome | null | undefined;
  compact?: boolean;
}): JSX.Element | null {
  if (!scan || scan.state !== "incomplete") return null;
  const parts: string[] = [];
  if (scan.comparisons != null && scan.comparisonLimit != null) {
    parts.push(`${scan.comparisons} confronti su ${scan.comparisonLimit}`);
  }
  if (scan.documentsUncompared != null && scan.documentsUncompared > 0) {
    parts.push(
      `${scan.documentsUncompared} ${
        scan.documentsUncompared === 1 ? "documento non confrontato" : "documenti non confrontati"
      }`,
    );
  }
  if (scan.chunks != null) parts.push(`${scan.chunks} sezioni`);
  return (
    <div
      data-testid="scan-incomplete"
      style={{
        display: "flex",
        alignItems: "baseline",
        gap: 6,
        flexWrap: "wrap",
        fontSize: compact ? 11 : 12,
        color: "var(--c-warn)",
        marginTop: compact ? 2 : 6,
      }}
    >
      <strong style={{ fontWeight: 600 }}>
        {/* `HON-6` (2026-09-13): this was ONE fixed string for two different
            facts. A shortfall made of comparisons that FAILED reaches no
            ceiling, and telling an administrator a limit was reached sends
            them to raise a budget that was never exhausted. An absent cause
            keeps the original sentence, which is what every pre-D-53 run
            means. */}
        {scan.cause === "failures"
          ? "Controllo incompleto — confronti non riusciti"
          : "Controllo incompleto — limite di confronti raggiunto"}
      </strong>
      {parts.length > 0 && <span style={{ color: "var(--c-ink-3)" }}>({parts.join(" · ")})</span>}
    </div>
  );
}
